/**
 * Formats a nightly rate for compact display inside a calendar day cell, per
 * TL-698. No currency symbol is included.
 *
 * Rules (by magnitude of the raw value):
 *   < 1,000        -> whole number ("888")
 *   1,000-999,999  -> divided by 1k, suffix "k" (lowercase)
 *   1M-999,999,999 -> divided by 1M, suffix "M"
 *   >= 1,000,000,000 -> divided by 1B, suffix "B"
 *
 * Within a suffix tier, the scaled value keeps one decimal place when it is
 * below 100 and no decimals once it reaches 100, giving roughly three
 * significant figures (e.g. 2,848 -> "2.8k", 18,488 -> "18.5k", 444,499 -> "444k").
 *
 * The "k" suffix is always lowercase; "M" and "B" are uppercase per the Jira examples.
 */
export function formatCalendarNightlyRate(price: number): string {
	const value = Math.round(price);

	if (value < 1_000) {
		return `${value}`;
	}

	const tiers: { divisor: number; suffix: string }[] = [
		{ divisor: 1_000_000_000, suffix: 'B' },
		{ divisor: 1_000_000, suffix: 'M' },
		{ divisor: 1_000, suffix: 'k' },
	];

	const { divisor, suffix } = tiers.find(tier => value >= tier.divisor) ?? tiers[2];
	const scaled = value / divisor;
	const decimals = scaled >= 100 ? 0 : 1;
	const formatted = scaled.toFixed(decimals).replace(/\.0$/, '');

	return `${formatted}${suffix}`;
}
